import { useEffect, useRef } from 'react';

/**
 * Attaches an IntersectionObserver to a sentinel element and calls `fetchNextPage`
 * when it becomes visible and more pages are available.
 *
 * Returns a ref to attach to the sentinel element.
 */
export function useInfiniteScroll({
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  scrollContainerRef,
}: {
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  /** The scrollable container element. When provided, the observer fires
   *  relative to that container (with 300px lookahead) instead of the viewport. */
  scrollContainerRef?: React.RefObject<HTMLElement | null>;
}) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const root = scrollContainerRef?.current ?? null;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root, threshold: 0, rootMargin: '0px 0px 600px 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, scrollContainerRef]);

  return sentinelRef;
}
